import { AlertTriangle, ShieldCheck, Signal, Waves, Wind, Timer, Users } from "lucide-react";
import { Link } from "react-router-dom";

const SUITABILITY_LABEL = {
  excellent_match: "Excellent match",
  good_match: "Good match",
  possible: "Possible",
  marginal: "Marginal",
  not_recommended: "Not recommended",
  insufficient_data: "Insufficient data",
};

const CONFIDENCE_LABEL = { high: "High confidence", moderate: "Moderate confidence", low: "Low confidence" };

const suitabilityColor = (s) => {
  switch (s) {
    case "excellent_match": return "bg-teal-600 text-white";
    case "good_match": return "bg-teal-800 text-white";
    case "possible": return "bg-sand-200 text-teal-900";
    case "marginal": return "bg-[#FDF3F0] text-terracotta-700";
    default: return "bg-sand-200 text-muted-foreground";
  }
};

const confidenceDots = (c) => {
  const filled = c === "high" ? 3 : c === "moderate" ? 2 : 1;
  return (
    <span className="inline-flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`w-1.5 h-1.5 rounded-full ${i < filled ? "bg-teal-800" : "bg-sand-300"}`}
        />
      ))}
    </span>
  );
};

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return iso; }
}

export default function RecommendationCard({ item, index = 0 }) {
  return (
    <Link
      to={`/surf-area/${item.surf_area_slug}?score=${item.score}`}
      data-testid={`rec-card-${item.surf_area_slug}`}
      className="block card-soft overflow-hidden hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(0,78,89,0.08)] transition-transform duration-300"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="flex flex-col md:flex-row">
        {item.image_url && (
          <div
            className="md:w-52 h-40 md:h-auto bg-cover bg-center flex-shrink-0"
            style={{ backgroundImage: `url(${item.image_url})` }}
            aria-hidden
          />
        )}
        <div className="flex-1 p-6 md:p-7 grid md:grid-cols-[1fr_auto] gap-6 items-start">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span
                data-testid={`rec-suitability-${item.surf_area_slug}`}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider ${suitabilityColor(item.suitability)}`}
              >
                {SUITABILITY_LABEL[item.suitability] || item.suitability}
              </span>
              <span
                data-testid={`rec-confidence-${item.surf_area_slug}`}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-white border border-border text-muted-foreground flex items-center gap-1.5"
              >
                {confidenceDots(item.confidence)} {CONFIDENCE_LABEL[item.confidence]}
              </span>
            </div>
            <h3 className="font-display text-2xl text-teal-900 leading-tight" data-testid={`rec-name-${item.surf_area_slug}`}>
              {item.surf_area_name}
            </h3>
            <div className="text-sm text-muted-foreground mt-0.5">
              {item.region}, {item.country}
              {item.travel_minutes != null && (
                <span className="inline-flex items-center gap-1 ml-3 text-xs">
                  <Timer className="w-3.5 h-3.5" />
                  {item.travel_minutes} min drive
                </span>
              )}
            </div>

            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="overline">Best window</div>
                <div className="text-teal-900 font-medium mt-0.5" data-testid={`rec-window-${item.surf_area_slug}`}>
                  {fmtTime(item.best_window_start)}–{fmtTime(item.best_window_end)}
                </div>
              </div>
              <div>
                <div className="overline">Waves</div>
                <div className="text-teal-900 font-medium mt-0.5 flex items-center gap-1">
                  <Waves className="w-3.5 h-3.5 text-teal-600" />
                  {item.expected.wave_category}
                </div>
              </div>
              <div>
                <div className="overline">Wind</div>
                <div className="text-teal-900 font-medium mt-0.5 flex items-center gap-1">
                  <Wind className="w-3.5 h-3.5 text-teal-600" />
                  {Math.round(item.expected.wind_speed_kmh)} km/h {item.expected.wind_direction}
                </div>
              </div>
              <div>
                <div className="overline">Crowd</div>
                <div className="text-teal-900 font-medium mt-0.5 flex items-center gap-1">
                  <Users className="w-3.5 h-3.5 text-teal-600" />
                  {item.crowd_estimate.replace("_", " ")}
                </div>
              </div>
            </div>

            {item.warnings?.length > 0 && (
              <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#FDF3F0] border border-[#F1D9CE] p-3">
                <AlertTriangle className="w-4 h-4 mt-0.5 text-terracotta-700 flex-shrink-0" />
                <div className="text-xs text-terracotta-700" data-testid={`rec-warnings-${item.surf_area_slug}`}>
                  {item.warnings[0]}
                  {item.warnings.length > 1 && ` · +${item.warnings.length - 1} more`}
                </div>
              </div>
            )}
          </div>

          <div className="flex md:flex-col items-end md:items-end md:justify-between gap-2 min-w-[110px]">
            <div className="text-right">
              <div className="overline">Surfmatics score</div>
              <div
                className="font-display text-5xl leading-none text-teal-900"
                data-testid={`rec-score-${item.surf_area_slug}`}
              >
                {item.score}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">of 100</div>
            </div>
            <span className="text-teal-800 text-xs font-semibold underline underline-offset-4">
              View details →
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
